import { ref, Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { httpService, type HttpError } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'
import { eventBus } from '../eventBus'
import {
  parseChatDetail,
  parseAgentEvent,
  type ChatMessageView,
} from './aiAgentContract'
import { agentActionBridge } from '../services/agentActionBridge'

/**
 * useAgentChat — the ACTIVE-THREAD composable for the in-app AI assistant.
 *
 * Holds the currently-open chat thread (`activeChatId` + its `messages`) and
 * exposes `loadThread(chatId)` and `send(text)`. The backend now persists each
 * chat (one row per turn) — the frontend no longer sends the full history; it
 * POSTs only the new `{ content }` to `/chats/:id/messages`, and the server
 * bounds the LLM context window itself (the last-N setting lives server-side).
 *
 * The backend now returns `text/event-stream` for the POST, emitting AgentEvent
 * frames. `send()` consumes the stream via `httpService.postStream` and updates
 * the active assistant placeholder reactively as frames arrive.
 *
 * Network access lives ONLY here (never in the .vue), honoring the
 * `no_httpservice_in_vue` qa rule and CLAUDE.md rules 3/5. Requests use an
 * ABSOLUTE URL to `config.api.agentHost`; httpService passes absolute URLs
 * through verbatim and attaches `Authorization: Bearer <jwt>` at the transport
 * layer.
 *
 * The user JWT is NEVER an argument to `send` — it is request-scoped and
 * transport-injected (the locked rule: the JWT is server/transport-injected,
 * never UI/model-visible). `send.length === 1` is asserted by the unit spec.
 *
 * `skipErrorDisplay` so this composable owns the error surface (`error.value` +
 * the assistant-error testid) instead of also firing the global toast. PT
 * messages are mapped here; the raw error text + requestId go to the structured
 * log only (never the JWT, never the diagnostics' PII-free body — there is none).
 */

/**
 * One segment of the live assistant turn's timeline. The stream interleaves the
 * model's reasoning trace ('reason') with its answer text ('output') across loop
 * iterations; we keep them as an ORDERED list (reason → output → reason → output …)
 * so the UI can show a chronological timeline and, when reasoning is hidden, stack
 * the output segments cleanly (each its own block — no run-on between iterations).
 */
export interface ChatSegment {
  type: 'reason' | 'output'
  text: string
}

/**
 * Streaming-aware extension of ChatMessageView.
 * The optional fields are set ONLY on the in-flight placeholder; persisted
 * messages (from loadThread) never carry them, keeping parseMessageView / the
 * chat-list / config paths untouched.
 */
export interface StreamingChatMessageView extends ChatMessageView {
  /** true while the SSE stream is still open for this assistant turn. */
  streaming?: boolean
  /** Set when a tool_start frame arrived and cleared by tool_result. */
  toolActivity?: { name: string } | null
  /** Ordered reason/output timeline, built live from the SSE stream. Set only on
   *  the in-flight placeholder; persisted/historical messages carry none (they
   *  render from `content`). */
  segments?: ChatSegment[]
}

// Synthetic ids used for the in-flight optimistic user turn and assistant
// placeholder. These never appear in persisted data (backend ids are positive
// integers). Used for id-based rollback filtering — Vue 3 wraps array items as
// reactive proxies, so reference equality (m !== obj) is unreliable.
const OPTIMISTIC_ID = -1
const PLACEHOLDER_ID = -2

export function useAgentChat() {
  const { t } = useI18n()
  const messages: Ref<StreamingChatMessageView[]> = ref([])
  const loading: Ref<boolean> = ref(false)
  const error: Ref<string | null> = ref(null)
  const activeChatId: Ref<number | null> = ref(null)

  function ptError(err: unknown): string {
    const status = (err as HttpError)?.status
    return status === 401
      ? t('Sua sessão expirou. Faça login novamente.')
      : t('Erro ao falar com o assistente. Tente novamente.')
  }

  async function loadThread(chatId: number): Promise<void> {
    loading.value = true
    error.value = null

    try {
      const raw: unknown = await httpService.get(`${config.api.agentHost}/chats/${chatId}`, {
        skipErrorDisplay: true,
        logContext: 'useAgentChat',
      })
      const detail = parseChatDetail(raw)
      activeChatId.value = detail.id
      messages.value = detail.messages
      log('useAgentChat', 'loadThread', 'Conversa carregada', {
        chatId: detail.id,
        count: detail.messages.length,
      })
    } catch (err) {
      error.value = ptError(err)
      const message = err instanceof Error ? err.message : String(err)
      log('useAgentChat', 'loadThread', 'Falha ao carregar conversa', {
        message,
        status: (err as HttpError)?.status,
        chatId,
      })
      throw err
    } finally {
      loading.value = false
    }
  }

  // Arity MUST stay 1 — the JWT is never a parameter (locked rule). Asserted by
  // the unit spec via `send.length === 1`.
  async function send(text: string): Promise<void> {
    const content = text.trim()
    const chatId = activeChatId.value
    if (!content || loading.value || chatId === null) {
      return
    }

    // Optimistic user turn so the UI updates immediately.
    const optimistic: StreamingChatMessageView = {
      id: OPTIMISTIC_ID,
      chatId,
      role: 'user',
      content,
      createdAt: new Date().toISOString(),
      diagnostics: null,
    }
    messages.value.push(optimistic)
    loading.value = true
    error.value = null

    // Streaming assistant placeholder — stable synthetic id (-2) for the whole
    // stream so the v-for key never changes mid-stream (no remount).
    // After push, Vue 3 wraps the object in a reactive Proxy (see comment at
    // the id-based filter below). All in-stream mutations MUST go through the
    // reactive proxy so Vue tracks the changes and schedules DOM updates.
    const placeholder: StreamingChatMessageView = {
      id: PLACEHOLDER_ID,
      chatId,
      role: 'assistant',
      content: '',
      createdAt: new Date().toISOString(),
      diagnostics: null,
      streaming: true,
      toolActivity: null,
      segments: [],
    }
    messages.value.push(placeholder)
    // Capture the reactive proxy (messages.value[i] !== placeholder — Vue wraps
    // array elements on first access). Mutations via `rp` are tracked by Vue and
    // trigger DOM updates; mutations via the original `placeholder` variable are not.
    const rp = messages.value[messages.value.length - 1]

    // Build the reason/output timeline. Consecutive frames of the same kind extend
    // the current segment; a kind switch (reason↔output) starts a new one — yielding
    // the chronological reason → output → reason → output … order. `pendingBreak`
    // (set on tool_start) forces the next segment to start fresh so two output bursts
    // either side of a tool call never run together. Mutates via the reactive proxy
    // `rp` so Vue re-renders incrementally while the stream is open.
    let pendingBreak = false
    const appendSegment = (type: ChatSegment['type'], text: string): void => {
      const segs = rp.segments ?? (rp.segments = [])
      const last = segs[segs.length - 1]
      if (!pendingBreak && last !== undefined && last.type === type) {
        last.text += text
      } else {
        segs.push({ type, text })
      }
      pendingBreak = false
    }

    // SSE telemetry — frame counts by type + timings (TTFT, duration), mirroring the
    // server-side sse_stream_close summary so a stream can be traced from both ends.
    const streamStartedAt = Date.now()
    let firstTokenAt: number | null = null
    const frameCounts: Record<string, number> = {}
    log('useAgentChat', 'send', 'SSE stream aberto', { chatId })

    try {
      const stream = httpService.postStream(
        `${config.api.agentHost}/chats/${chatId}/messages`,
        { content },
        { skipErrorDisplay: true, logContext: 'useAgentChat' },
      )

      for await (const frameJson of stream) {
        let parsed: unknown
        try {
          parsed = JSON.parse(frameJson)
        } catch {
          // Malformed frame — not valid JSON; treat as boundary drift.
          throw new Error(`Frame SSE inválido (JSON malformado): ${frameJson.slice(0, 80)}`)
        }

        const event = parseAgentEvent(parsed)
        frameCounts[event.type] = (frameCounts[event.type] ?? 0) + 1

        if (event.type === 'reason') {
          // Reasoning trace — its own timeline segment, separate from the answer.
          appendSegment('reason', event.text)
        } else if (event.type === 'token') {
          if (firstTokenAt === null) {
            firstTokenAt = Date.now()
          }
          // Keep `content` as the full answer text (persistence / historical render),
          // and add the token to the output timeline segment.
          rp.content += event.text
          appendSegment('output', event.text)
        } else if (event.type === 'tool_start') {
          rp.toolActivity = { name: event.name }
          // A tool call delimits a loop iteration — force the next segment to start
          // fresh so outputs on either side never glue together.
          pendingBreak = true
        } else if (event.type === 'tool_result') {
          rp.toolActivity = null
        } else if (event.type === 'done') {
          rp.streaming = false
          rp.toolActivity = null
          rp.diagnostics = event.diagnostics
          // Reconcile the id ONLY here (never mid-stream) to avoid remounting
          // the v-for bubble while the stream is open.
          rp.id = event.messageId
          log('useAgentChat', 'send', 'SSE stream concluído', {
            chatId,
            requestId: event.diagnostics.requestId,
            frames: frameCounts,
            ttftMs: firstTokenAt !== null ? firstTokenAt - streamStartedAt : null,
            durationMs: Date.now() - streamStartedAt,
          })
          // Notify wallet to re-fetch (Option A — done frame carries no balance).
          eventBus.emit('agent.turn.done', { chatId })
        } else if (event.type === 'error') {
          // The server signalled an application-level error in-stream.
          log('useAgentChat', 'send', 'SSE stream erro (in-stream)', {
            message: `${event.code}: ${event.message}`,
            chatId,
            frames: frameCounts,
            durationMs: Date.now() - streamStartedAt,
          })
          // Roll back the placeholder + the optimistic user turn.
          // Use id-based comparison: Vue 3 wraps array items as reactive proxies
          // so reference equality (m !== placeholder) fails. The synthetic ids
          // (-1 for optimistic, -2 for placeholder) never appear in persisted data.
          messages.value = messages.value.filter(
            (m) => m.id !== OPTIMISTIC_ID && m.id !== PLACEHOLDER_ID,
          )
          error.value = ptError(null)
          return
        } else if (event.type === 'ui_action') {
          // Non-fatal side-effect: dispatch to the bridge (allow-list + payload guard).
          // Must NOT touch placeholder, must NOT roll back, must NOT set error.
          agentActionBridge.dispatch(event)
        }
      }
    } catch (err) {
      const status = (err as HttpError)?.status
      if (status === 402) {
        // 402 = insufficient credits — keep the optimistic/placeholder bubbles so
        // the thread survives and the user can switch franchise + retry.
        eventBus.emit('agent.credits.insufficient', { chatId })
        log('useAgentChat', 'send', 'Créditos insuficientes (402)', { chatId })
      } else {
        // Pre-stream HttpError (e.g. 401/500) or a boundary-guard parse failure.
        const message = err instanceof Error ? err.message : String(err)
        messages.value = messages.value.filter(
          (m) => m.id !== OPTIMISTIC_ID && m.id !== PLACEHOLDER_ID,
        )
        error.value = ptError(err)
        log('useAgentChat', 'send', 'Falha ao enviar mensagem', {
          message,
          status,
          chatId,
        })
      }
    } finally {
      loading.value = false
    }
  }

  return {
    messages,
    loading,
    error,
    activeChatId,
    loadThread,
    send,
  }
}
